import React from 'react';
import { ISubmission, SubmissionStatus, SubmissionType } from 'btdt-types';
import useApiClient from '../useApiClient';
import SubmissionDetail from './SubmissionDetail';
import './SubmissionsIndexScreen.css';

const STATUS_LABELS: Record<string, string> = {
  all: 'הכל',
  [SubmissionStatus.PENDING]: 'ממתינות',
  [SubmissionStatus.APPROVED]: 'אושרו',
  [SubmissionStatus.REJECTED]: 'נדחו',
};

const STATUS_CLASS: Record<string, string> = {
  [SubmissionStatus.PENDING]: 'status-pending',
  [SubmissionStatus.APPROVED]: 'status-approved',
  [SubmissionStatus.REJECTED]: 'status-rejected',
};

type SubmissionWithVenue = ISubmission & { venue?: { name: string; nameEn?: string; address?: string; yearOpened?: number; yearClosed?: number | null; tags?: string[] } };

const SubmissionsIndexScreen: React.FC = () => {
  const { getApiClient } = useApiClient();
  const [submissions, setSubmissions] = React.useState<SubmissionWithVenue[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [filter, setFilter] = React.useState<string>('all');
  const [expandedId, setExpandedId] = React.useState<number | null>(null);

  const fetchSubmissions = React.useCallback(async (statusFilter: string) => {
    try {
      const client = await getApiClient();
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const response = await client.get<{ result: SubmissionWithVenue[] }>('/api/submission', { params });
      setSubmissions(response.data.result);
    } catch (error) {
      console.error('Failed to fetch submissions:', error);
    } finally {
      setIsLoading(false);
    }
  }, [getApiClient]);

  React.useEffect(() => {
    fetchSubmissions(filter);
  }, [fetchSubmissions, filter]);

  const handleApprove = async (id: number) => {
    if (!window.confirm('לאשר את ההגשה?')) return;
    try {
      const client = await getApiClient();
      await client.post(`/api/submission/${id}/approve`);
      setSubmissions((prev) => prev.map((s) => s.id === id ? { ...s, status: SubmissionStatus.APPROVED } : s));
    } catch (error) {
      console.error('Failed to approve submission:', error);
    }
  };

  const handleReject = async (id: number) => {
    if (!window.confirm('לדחות את ההגשה?')) return;
    try {
      const client = await getApiClient();
      await client.post(`/api/submission/${id}/reject`);
      setSubmissions((prev) => prev.map((s) => s.id === id ? { ...s, status: SubmissionStatus.REJECTED } : s));
    } catch (error) {
      console.error('Failed to reject submission:', error);
    }
  };

  const handleUpdate = async (updated: ISubmission) => {
    try {
      const client = await getApiClient();
      await client.put(`/api/submission/${updated.id}`, {
        venueData: updated.venueData,
        source: updated.source,
        photoCredits: updated.photoCredits,
        photoCaptions: updated.photoCaptions,
      });
      setSubmissions((prev) => prev.map((s) => s.id === updated.id ? { ...s, ...updated } : s));
    } catch (error) {
      console.error('Failed to update submission:', error);
    }
  };

  if (isLoading) {
    return <div>טוענת...</div>;
  }

  return (
    <>
      <div className="submissionsIndexScreen-header">
        <h1>ניהול הגשות</h1>
        <div className="submissionsIndexScreen-filters">
          {Object.entries(STATUS_LABELS).map(([key, label]) => (
            <button
              key={key}
              className={filter === key ? 'active' : ''}
              onClick={() => { setFilter(key); setIsLoading(true); }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <table className="submissionsTable">
        <thead>
          <tr>
            <th>סוג</th>
            <th>מקום</th>
            <th>שולח</th>
            <th>תאריך</th>
            <th>סטטוס</th>
            <th></th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {submissions.map((submission) => (
            <React.Fragment key={submission.id}>
              <tr>
                <td>
                  <span className={`type-badge ${submission.type === SubmissionType.SUGGEST_EDIT ? 'edit' : 'new'}`}>
                    {submission.type === SubmissionType.SUGGEST_EDIT ? 'תיקון' : 'מקום חדש'}
                  </span>
                </td>
                <td>{submission.venueData?.name || submission.venue?.name || '—'}</td>
                <td>{submission.submitterName || '—'}</td>
                <td dir="ltr">{new Date(submission.createdAt).toLocaleDateString('he-IL')}</td>
                <td className={STATUS_CLASS[submission.status] || ''}>{STATUS_LABELS[submission.status] || submission.status}</td>
                <td>
                  <button className="expand-btn" onClick={() => setExpandedId(expandedId === submission.id ? null : submission.id)}>
                    {expandedId === submission.id ? 'סגור' : 'פרטים'}
                  </button>
                </td>
                <td>
                  {submission.status === SubmissionStatus.PENDING && (
                    <>
                      <button className="action-btn approve-btn" onClick={() => handleApprove(submission.id)}>אישור</button>
                      <button className="action-btn reject-btn" onClick={() => handleReject(submission.id)}>דחייה</button>
                    </>
                  )}
                </td>
              </tr>
              {expandedId === submission.id && (
                <tr>
                  <td colSpan={7} style={{ padding: 0 }}>
                    <SubmissionDetail submission={submission} onUpdate={handleUpdate} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </>
  );
};

export default SubmissionsIndexScreen;
